import { AddressItemType, createForwardingBatch, PostageUsageType } from '@ukpostbox/client-data';
import { CreateForwardingBatchType } from '@ukpostbox/client-data/lib/Types/ForwardingBatchTypes';
import {
	ButtonSubmit,
	ColourStateSolid,
	FlexGap,
	FormWrapperLabel,
	Modal,
	StackVertical,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { Controller, FieldValues, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { AddressControl } from '../../../_Global/Components/Address/AddressControl';
import { EndpointError } from '../../../_Global/Components/EndpointError';
import { PostageControl } from '../../../_Global/Components/PostageControl';
import ListViewMailItemMinimal from '../Mailbox/Entries/ListViewMailItemMinimal';
import { ForwardItemsModalType } from '../Types/ForwardItemTypes';

function ForwardItemsModal(props: ForwardItemsModalType) {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [activeAddress, setActiveAddress] = useState<AddressItemType | null>(null);
	const [resetForm, setResetForm] = useState<boolean>(false);

	const [forwardingActive, setForwardingActive] = useState<boolean>(false);
	const [forwardingError, setForwardingError] = useState<any>(null);

	const formMethods = useForm({
		mode: 'onChange',
		defaultValues: {
			mailUuids: props.mailIds,
			addressId: -1,
			postageOptionId: -1,
		},
	});

	const HandleSubmit = (data: FieldValues) => {
		setForwardingActive(true);
		createForwardingBatch(data as CreateForwardingBatchType)
			.then((r) => {
				navigate(`/mail/forwarding-batches/${r.batchId}`);
				props.actionedCallback();
				setForwardingActive(false);
			})
			.catch((e) => {
				setForwardingError(e);
				setForwardingActive(false);
			});
	};

	useEffect(() => {
		formMethods.setValue('mailUuids', props.mailIds);
	}, [props.isOpen]);

	useEffect(() => {
		setResetForm(!resetForm);
	}, []);

	return (
		<Modal
			title={t('forwardingBatches.create.title')}
			isOpen={props.isOpen}
			closeCallback={() => props.closeCallback()}
			actionedCallback={() => props.actionedCallback()}
		>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(HandleSubmit)}>
					<fieldset disabled={forwardingActive}>
						<StackVertical gap={FlexGap.Medium}>
							{forwardingError && <EndpointError {...forwardingError} />}
							<div>
								<label className='mb-xs'>
									{t('forwardingBatches.create.labels.mailItems', { count: props.mailIds.length })}
								</label>
								{props.mailItems.map((item) => (
									<ListViewMailItemMinimal {...item} callBack={null} forwardingPreview />
								))}
							</div>

							<Controller
								control={formMethods.control}
								name='addressId'
								rules={{ min: 0 }}
								render={({ field, fieldState }) => (
									<FormWrapperLabel
										label={t('forwardingBatches.create.labels.address')}
										invalid={fieldState.invalid}
										validationMessage={fieldState.error?.message}
									>
										<AddressControl
											name={field.name}
											showCreateAddressOptions
											singleSelection
											forwardItemCallback={(e) => {
												setActiveAddress(e);
												field.onChange(e.value);
											}}
											defaultValues={field.value !== -1 ? [field.value] : undefined}
											minimal
											required
											reset={resetForm}
										/>
									</FormWrapperLabel>
								)}
							/>

							<Controller
								control={formMethods.control}
								name='postageOptionId'
								rules={{ min: 0 }}
								render={({ field, fieldState }) => (
									<FormWrapperLabel
										label={t('forwardingBatches.create.labels.postage')}
										invalid={fieldState.invalid}
										validationMessage={fieldState.error?.message}
									>
										<PostageControl
											postageType={PostageUsageType.Forwarding}
											name={field.name}
											activeAddress={activeAddress}
											alwaysEnabled
											singleSelection
											forwardValueCallback={(e) => {
												field.onChange(e);
											}}
											defaultValues={field.value !== -1 ? [field.value] : undefined}
											minimal
											required
											reset={resetForm}
										/>
									</FormWrapperLabel>
								)}
							/>

							<ButtonSubmit
								title={t('forwardingBatches.create.actions.create')}
								fullWidth
								colour={ColourStateSolid.Primary}
								loading={forwardingActive}
								disabled={forwardingActive || !formMethods.formState.isValid}
							/>
						</StackVertical>
					</fieldset>
				</form>
			</FormProvider>
		</Modal>
	);
}

export { ForwardItemsModal };
